import { useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useRouteKeyStore } from '@/store/routeKeyStore'
import navigationConfig from '@/configs/navigation.config'

/**
 * Derive a navigation route key from the current URL path and keep
 * the routeKeyStore in sync.
 *
 * Static nav items are matched by their `path` in `navigationConfig`.
 * Dynamic note pages (`/app/note/{uuid}`) map to `recent.{uuid}` so
 * the Recent sidebar section highlights correctly.
 */
function resolveRouteKey(pathname: string, search: string): string {
    // Tags filter active on the notes index → highlight the tags section
    const params = new URLSearchParams(search)
    if (pathname === '/app' && params.has('tag')) {
        return 'tags'
    }

    // Check static navigation config first (exact match)
    const staticMatch = navigationConfig.find(
        (nav) => nav.path === pathname,
    )
    if (staticMatch) {
        return staticMatch.key
    }

    // Dynamic: /app/note/{uuid}
    const noteMatch = pathname.match(/^\/app\/note\/([^/]+)$/)
    if (noteMatch) {
        return `recent.${noteMatch[1]}`
    }

    return ''
}

export function useRouteKeySync() {
    const setCurrentRouteKey = useRouteKeyStore(
        (state) => state.setCurrentRouteKey,
    )

    useEffect(() => {
        // Set on initial mount
        setCurrentRouteKey(resolveRouteKey(window.location.pathname, window.location.search))

        // Update on every Inertia navigation
        return router.on('navigate', () => {
            setCurrentRouteKey(resolveRouteKey(window.location.pathname, window.location.search))
        })
    }, [setCurrentRouteKey])
}


